import React, { useState, useRef, useEffect } from 'react';
import filter from 'lodash/filter';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import { getRouteColor, getLatLng, retryOperation, routeOrdersToStages, RouteElementType, isHiddenFromMap } from './../utils/utils';

/**
 * Decode a Google-encoded polyline string into an array of [lng, lat] pairs.
 * Algorithm: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
function decodeGooglePolyline(encoded) {
  if (!encoded) return [];
  const coords = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    coords.push([lng / 1e5, lat / 1e5]);
  }

  return coords;
}

/**
 * Extract detailed route coordinates from a Google DirectionsRoute.
 * Tries multiple sources in order of detail/reliability:
 *   1. Step-level paths (most detailed, follows roads exactly)
 *   2. overview_path (decoded convenience property)
 *   3. overview_polyline.points (encoded string, manually decoded)
 * Returns [lng, lat] pairs (OSRM-compatible format).
 */
function extractGoogleRouteCoordinates(googleRoute) {
  // 1. Step-level paths — always follow roads, most detailed
  if (googleRoute.legs) {
    const stepCoords = [];
    googleRoute.legs.forEach(leg => {
      if (!leg.steps) return;
      leg.steps.forEach(step => {
        const path = step.path || step.lat_lngs;
        if (path && path.length > 0) {
          path.forEach(p => {
            if (typeof p.lng === 'function') {
              stepCoords.push([p.lng(), p.lat()]);
            } else if (typeof p.lng === 'number') {
              stepCoords.push([p.lng, p.lat]);
            }
          });
        }
      });
    });
    if (stepCoords.length > 0) return stepCoords;
  }

  // 2. overview_path — simplified road-following polyline
  if (googleRoute.overview_path && googleRoute.overview_path.length > 0) {
    const overviewCoords = [];
    googleRoute.overview_path.forEach(p => {
      if (typeof p.lng === 'function') {
        overviewCoords.push([p.lng(), p.lat()]);
      } else if (typeof p.lng === 'number') {
        overviewCoords.push([p.lng, p.lat]);
      }
    });
    if (overviewCoords.length > 0) return overviewCoords;
  }

  // 3. Decode encoded polyline string
  if (googleRoute.overview_polyline) {
    const encoded = typeof googleRoute.overview_polyline === 'string'
      ? googleRoute.overview_polyline
      : googleRoute.overview_polyline.points;
    const decoded = decodeGooglePolyline(encoded);
    if (decoded.length > 0) return decoded;
  }

  return [];
}

/**
 * Extract route polyline coordinates from a normalized directions result.
 * Both Google and OSRM engines produce geometry.coordinates as [lng, lat] —
 * we convert to [lat, lng] for Leaflet/MapLibre rendering.
 */
function extractPolylineFromDirections(directions) {
  if (!directions || !directions.routes || directions.routes.length === 0) return [];
  const route = directions.routes[0];

  if (route.geometry && route.geometry.coordinates && route.geometry.coordinates.length > 0) {
    return route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }

  return [];
}

/**
 * Parse a via-point into a plain {lat, lng} object (used by Google engine).
 */
const getPoint = (point) => {
  if (isObject(point) && typeof point.lat === 'string') {
    return { lat: parseFloat(point.lat), lng: parseFloat(point.lng) };
  }
  if (isArray(point)) {
    const [lat, lng] = point;
    return { lat: parseFloat(lat), lng: parseFloat(lng) };
  }
  return point;
};

export const useMapApi = ({
  editable = false,
  routingEngine = 'google',
  api = null,
  startPoint,
  departureTime = new Date(Date.now()),
  endPoint,
  routeOrders,
  hideInactiveRouteOrders = true,
  onUpdateRouteOrder = () => {},
  onUpdateRouteOrders = () => {},
  handleBeforeApiCall = () => {},
  onStart = () => {},
  onFinish = () => {},
}) => {
  const [startPointLocation, setStartPointLocation] = useState(null);
  const [endPointLocation, setEndPointLocation] = useState(null);
  const [routePolylines, setRoutePolylines] = useState([]);
  const [snappedPositions, setSnappedPositions] = useState({});
  const [roadSnappedPositions, setRoadSnappedPositions] = useState({});
  const [routeDistance, setRouteDistance] = useState(0);
  const [routeDuration, setRouteDuration] = useState(0);

  // Google-specific state
  const [googleDirectionsService, setGoogleDirectionsService] = useState(null);

  const prevBreaksRef = useRef([]);

  useEffect(() => {
    const next = routeOrders?.filter(e => e.type === RouteElementType.DRIVER_BREAK_TIME);

    const same =
      prevBreaksRef.current.length === next.length &&
      prevBreaksRef.current.every((x, i) => x === next[i]);

    if (!same) {
      prevBreaksRef.current = next;
      handleBeforeApiCall(next);
    }
  }, [routeOrders, handleBeforeApiCall]);

  const stages = routeOrdersToStages(routeOrders?.filter((element) => element.type === RouteElementType.ROUTE_ORDER));

  // Snapshot driver-break positions in a stage so we can re-anchor them after
  // Google/OSRM renumber ROUTE_ORDERs from 0..N-1. Each break's "rank" is the
  // number of ROUTE_ORDERs that preceded it in the pre-optimize sequence.
  const getStageBreakRanks = (stageIndex) => {
    const stageKey = String(stageIndex);
    const stageOrders = (routeOrders ?? [])
      .filter(ro => ro.type === RouteElementType.ROUTE_ORDER && String(ro.stage) === stageKey);
    const stageBreaks = (routeOrders ?? [])
      .filter(ro => ro.type === RouteElementType.DRIVER_BREAK_TIME && String(ro.stage) === stageKey);

    return stageBreaks
      .map(brk => ({
        uuid: brk.uuid,
        rank: stageOrders.filter(ro => ro.ordering < brk.ordering).length,
        origOrdering: brk.ordering,
      }))
      .sort((a, b) => a.rank - b.rank || a.origOrdering - b.origOrdering);
  };

  // Apply the new break orderings using a functional state update so it
  // composes with the per-waypoint singular updates fired in the same tick.
  const reanchorBreaks = (breakRanks) => {
    if (breakRanks.length === 0) return;
    onUpdateRouteOrders(prev => (prev ?? []).map(ro => {
      if (ro.type !== RouteElementType.DRIVER_BREAK_TIME) return ro;
      const idx = breakRanks.findIndex(b => b.uuid === ro.uuid);
      if (idx === -1) return ro;
      return { ...ro, ordering: breakRanks[idx].rank + idx };
    }));
  };

  // ---------------------------------------------------------------------------
  // Shared: render all stages on the map
  // ---------------------------------------------------------------------------
  const renderStagesOnMap = async (optimizeWaypoints = false) => {
    onStart();

    let startPointLocationData, endPointLocationData;
    try {
      ({ startPointLocationData, endPointLocationData } = await getEdgePointsLocations());
    } catch (err) {
      onFinish(false);
      return null;
    }

    if (!startPointLocationData.point) {
      onFinish();
      toastr.error(__('routes.no_start_point'));
      return null;
    }

    if (optimizeWaypoints) {
      setEndPointLocation(prev => ({ ...prev, via: [] }));
    }

    setRoutePolylines([]);

    const keys = Object.keys(stages);
    const values = Object.values(stages);

    const data = await Promise.all(
      keys.map(async (key, index) => {
        const stage = stages[key];
        const stageWaypoints = [];

        if (index === 0) {
          stageWaypoints.push({ ...startPointLocationData, type: 'startPoint' });
        } else {
          const previousStage = values[index - 1];
          // Use the last ROUTE_ORDER element (DRIVER_BREAK_TIME has no real coords).
          const routeOrder = [...previousStage]
            .reverse()
            .find(e => e.type !== RouteElementType.DRIVER_BREAK_TIME);
          stageWaypoints.push({ type: 'point', routeOrder, point: getLatLng(routeOrder.order) });
        }

        stage
          .filter((routeOrder) => routeOrder.type !== RouteElementType.DRIVER_BREAK_TIME)
          .filter((routeOrder) => !hideInactiveRouteOrders || !isHiddenFromMap(routeOrder.order))
          .forEach(routeOrder => {
            stageWaypoints.push({ type: 'point', routeOrder, point: getLatLng(routeOrder.order) });
          });

        if (index === (values.length - 1) && endPointLocationData.point) {
          stageWaypoints.push({ ...endPointLocationData, type: 'endPoint' });
        }

        const origin = stageWaypoints.shift();

        return retryOperation(
          calculateAndRenderRouteOnMap,
          { optimizeWaypoints, index: parseInt(key), origin, stageWaypoints },
          2000,
          1
        );
      })
    );

    const errors = filter(data, ({ status }) => status === 'error');
    onFinish(false);

    if (errors.length) {
      setRoutePolylines([]);
      errors.forEach(({ error, index }) => {
        toastr.error(__('routes.map_state_error', { index: index }));
      });
      return null;
    }

    // Build polylines from normalized directions results
    const polylines = data
      .filter(d => d && d.status === 'success' && d.directions)
      .map(d => ({
        stageIndex: d.stageIndex,
        color: getRouteColor(d.stageIndex),
        positions: extractPolylineFromDirections(d.directions),
      }));

    // Compute total distance (meters) and duration (seconds) from all stages
    let totalDistanceM = 0;
    let totalDurationS = 0;
    data
      .filter(d => d && d.status === 'success' && d.directions)
      .forEach(d => {
        const route = d.directions.routes[0];
        if (route) {
          (route.legs || []).forEach(leg => {
            totalDistanceM += leg.distance || 0;
            totalDurationS += leg.duration || 0;
          });
        }
      });

    const nextRouteDistance = Math.round((totalDistanceM / 1000) * 10) / 10;
    const nextRouteDuration = totalDurationS;

    // Snapped positions from waypoints
    const snapped = {};
    data
      .filter(d => d && d.status === 'success' && d.orderedWaypoints)
      .forEach(d => {
        (d.orderedWaypoints || []).forEach(wp => {
          if (!wp || !wp.routeOrder || !wp.routeOrder.order || !wp.point) return;
          snapped[wp.routeOrder.order.id] = {
            lat: parseFloat(wp.point.lat),
            lng: parseFloat(wp.point.lng),
          };
        });
      });

    // Road-snapped positions from OSRM waypoints (where the route actually touches the road)
    const roadSnapped = {};
    data
      .filter(d => d && d.status === 'success' && d.roadSnappedWaypoints)
      .forEach(d => {
        Object.assign(roadSnapped, d.roadSnappedWaypoints);
      });

    const routeResult = {
      data,
      polylines,
      snappedPositions: snapped,
      roadSnappedPositions: roadSnapped,
      routeDistance: nextRouteDistance,
      routeDuration: nextRouteDuration,
    };

    setRoutePolylines(routeResult.polylines);
    setSnappedPositions(routeResult.snappedPositions);
    setRoadSnappedPositions(routeResult.roadSnappedPositions);
    setRouteDistance(routeResult.routeDistance);
    setRouteDuration(routeResult.routeDuration);

    return routeResult;
  };

  // ---------------------------------------------------------------------------
  // Shared: geocode via backend (works for both engines)
  // ---------------------------------------------------------------------------
  const geocodingProvider = routingEngine === 'osrm' ? 'nominatim' : 'google';

  const parseAddress = (address) => {
    let street = address;
    let house = '';
    let zip = '';
    let city = '';

    const parts = address.split(',').map(s => s.trim());
    if (parts.length >= 2) {
      const streetParts = parts[0].split(/\s+/);
      house = streetParts.pop() || '';
      street = streetParts.join(' ') || parts[0];

      const remainingParts = parts.slice(1);
      for (const part of remainingParts) {
        const tokens = part.split(/\s+/);
        const zipIndex = tokens.findIndex(t => /^\d{2}-\d{3}$/.test(t));
        if (zipIndex !== -1) {
          zip = tokens[zipIndex];
          tokens.splice(zipIndex, 1);
          city = tokens.join(' ');
          break;
        }
      }

      if (!zip && !city) {
        city = remainingParts[0] || '';
      }
    }

    return { street, house, zip, city };
  };

  const sessionCacheKey = (address) => `geocode:${address}`;

  const getEdgePointsLocations = async () => {
    const startAddr = startPoint.address;
    const endAddr = endPoint.address;

    // Check sessionStorage cache first
    let startPointCords = null;
    let endPointCords = null;

    if (startAddr) {
      try {
        const cached = sessionStorage.getItem(sessionCacheKey(startAddr));
        if (cached) startPointCords = JSON.parse(cached);
      } catch (e) { /* ignore */ }
    }

    if (endAddr) {
      try {
        const cached = sessionStorage.getItem(sessionCacheKey(endAddr));
        if (cached) endPointCords = JSON.parse(cached);
      } catch (e) { /* ignore */ }
    }

    // Batch-fetch any missing geocodes in a single HTTP request
    const toFetch = [];
    if (startAddr && !startPointCords) toFetch.push({ address: startAddr, parsed: parseAddress(startAddr), target: 'start' });
    if (endAddr && !endPointCords) toFetch.push({ address: endAddr, parsed: parseAddress(endAddr), target: 'end' });

    if (toFetch.length > 0) {
      try {
        const response = await window.axios.post('/admin/geocoding/batch', {
          addresses: toFetch.map(f => f.parsed),
          provider: geocodingProvider,
        });

        const results = response.data?.results || [];
        toFetch.forEach((f, i) => {
          const r = results[i];
          if (r && r.lat != null && r.lng != null) {
            const coords = { lat: r.lat, lng: r.lng };
            try { sessionStorage.setItem(sessionCacheKey(f.address), JSON.stringify(coords)); } catch (e) { /* ignore */ }
            if (f.target === 'start') startPointCords = coords;
            if (f.target === 'end') endPointCords = coords;
          }
        });
      } catch (err) {
        console.warn('Batch geocoding failed:', err);
      }
    }

    const startPointLocationData = { ...startPoint, point: startPointCords };
    const endPointLocationData = { ...endPoint, point: endPointCords };

    setStartPointLocation(startPointLocationData);
    setEndPointLocation(endPointLocationData);

    return { startPointLocationData, endPointLocationData };
  };

  // ---------------------------------------------------------------------------
  // OSRM engine: route calculation via public OSRM API
  // ---------------------------------------------------------------------------
  const calculateRouteOsrm = ({ optimizeWaypoints, index, origin, stageWaypoints }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const routeWaypoints = [...stageWaypoints];
        const destination = routeWaypoints.pop();

        const orderedWaypointsSnapshot = [...routeWaypoints];
        if (destination.type !== 'endPoint' && destination.routeOrder) {
          orderedWaypointsSnapshot.push(destination);
        }

        const allPoints = [origin, ...routeWaypoints, destination].filter(p => p?.point?.lat && p?.point?.lng);

        if (allPoints.length < 2) {
          return reject({ status: 'error', error: 'Not enough points', index });
        }

        const coords = allPoints.map(p => `${p.point.lng},${p.point.lat}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=false`;

        const response = await fetch(url);
        if (!response.ok) {
          return reject({ status: 'error', error: `OSRM HTTP ${response.status}`, index });
        }

        const data = await response.json();

        if (data.code !== 'Ok' || !data.routes?.length) {
          return reject({ status: 'error', error: `OSRM code: ${data.code}`, index });
        }

        const route = data.routes[0];

        const directions = {
          routes: [{
            legs: route.legs || [],
            geometry: route.geometry,
            distance: route.distance || 0,
            duration: route.duration || 0,
          }],
          stageIndex: index,
        };

        const result = { status: 'success', directions, stageIndex: index };
        result.orderedWaypoints = orderedWaypointsSnapshot;

        // Store road-snapped waypoint positions from OSRM response
        if (data.waypoints && data.waypoints.length > 0) {
          result.roadSnappedWaypoints = {};
          // allPoints[0] = origin, allPoints[last] = destination
          // OSRM waypoints correspond 1:1 with allPoints
          allPoints.forEach((p, i) => {
            const wp = data.waypoints[i];
            if (wp && wp.location && p.routeOrder && p.routeOrder.order) {
              result.roadSnappedWaypoints[p.routeOrder.order.id] = {
                lat: wp.location[1],
                lng: wp.location[0],
              };
            }
          });
        }

        if (optimizeWaypoints) {
          const breakRanks = getStageBreakRanks(index);
          routeWaypoints.forEach((wp, i) => {
            if (wp.routeOrder) {
              const bumps = breakRanks.filter(b => b.rank <= i).length;
              onUpdateRouteOrder(wp.routeOrder.order.id, { ordering: i + bumps, via: [] });
            }
          });
          reanchorBreaks(breakRanks);
        }

        resolve(result);
      } catch (err) {
        reject({ status: 'error', error: err.message || 'OSRM fetch failed', index });
      }
    });
  };

  // ---------------------------------------------------------------------------
  // Google engine: route calculation via Google Maps Directions API
  // ---------------------------------------------------------------------------
  const getGoogleWaypoints = ({ routeWaypoints, optimizeWaypoints, destination }) => {
    const waypoints = [];

    routeWaypoints.forEach(({ routeOrder }) => {
      if (!optimizeWaypoints && routeOrder.via && routeOrder.via.length > 0) {
        routeOrder.via.forEach((point) => {
          waypoints.push({ location: getPoint(point), stopover: false });
        });
      }
      waypoints.push({ location: getLatLng(routeOrder.order), stopover: true });
    });

    if (!optimizeWaypoints) {
      const viaPoints = destination.type === 'endPoint' ? (destination.via || []) : (destination.routeOrder?.via || []);
      if (viaPoints.length > 0) {
        viaPoints.forEach((point) => {
          waypoints.push({ location: getPoint(point), stopover: false });
        });
      }
    }

    return waypoints;
  };

  const calculateRouteGoogle = ({ optimizeWaypoints, index, origin, stageWaypoints }) => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps) {
        return reject({ status: 'error', error: 'Google Maps API not loaded', index });
      }

      const routeWaypoints = [...stageWaypoints];
      const destination = routeWaypoints.pop();

      const orderedWaypointsSnapshot = [...routeWaypoints];
      if (destination.type !== 'endPoint' && destination.routeOrder) {
        orderedWaypointsSnapshot.push(destination);
      }

      let directionsService = googleDirectionsService;
      if (!directionsService) {
        directionsService = new window.google.maps.DirectionsService();
        setGoogleDirectionsService(directionsService);
      }

      directionsService.route(
        {
          origin: origin.point,
          destination: destination.point,
          drivingOptions: {
            departureTime: new Date(Math.max(departureTime.getTime(), Date.now())),
            trafficModel: 'bestguess',
          },
          waypoints: getGoogleWaypoints({ routeWaypoints, optimizeWaypoints, destination }),
          optimizeWaypoints,
          travelMode: 'DRIVING',
          avoidTolls: true,
          avoidFerries: true,
        },
        function (response, status) {
          if (status === 'OK') {
            // Normalize Google response to match OSRM format
            const googleRoute = response.routes[0];
            const coordinates = extractGoogleRouteCoordinates(googleRoute);
            const directions = {
              routes: [{
                legs: googleRoute.legs.map(leg => ({
                  distance: leg.distance.value,
                  duration: leg.duration.value,
                })),
                geometry: {
                  coordinates,
                },
              }],
              stageIndex: index,
            };

            const result = { status: 'success', directions, stageIndex: index };
            result.orderedWaypoints = orderedWaypointsSnapshot;

            // Extract road-snapped waypoint positions from Google legs.
            // Each leg ends at a stopover waypoint (or destination).
            // leg[0].end_location = waypoint[0], leg[1].end_location = waypoint[1], etc.
            // leg[last].end_location = destination
            result.roadSnappedWaypoints = {};
            if (googleRoute.legs && googleRoute.legs.length > 0) {
              // Determine effective waypoint order
              const wpOrder = (optimizeWaypoints && googleRoute.waypoint_order)
                ? googleRoute.waypoint_order
                : routeWaypoints.map((_, i) => i);

              // Map each leg's end_location to the corresponding route order
              wpOrder.forEach((wpIndex, legIndex) => {
                const wp = routeWaypoints[wpIndex];
                const leg = googleRoute.legs[legIndex];
                if (wp && wp.routeOrder && wp.routeOrder.order && leg && leg.end_location) {
                  const loc = leg.end_location;
                  result.roadSnappedWaypoints[wp.routeOrder.order.id] = {
                    lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
                    lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
                  };
                }
              });

              // Destination (last leg end) — may also be a route order
              const lastLeg = googleRoute.legs[googleRoute.legs.length - 1];
              if (destination.routeOrder && destination.routeOrder.order && lastLeg && lastLeg.end_location) {
                const loc = lastLeg.end_location;
                result.roadSnappedWaypoints[destination.routeOrder.order.id] = {
                  lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
                  lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
                };
              }
            }

            if (optimizeWaypoints && googleRoute.waypoint_order) {
              const breakRanks = getStageBreakRanks(index);
              googleRoute.waypoint_order.forEach((wpIndex, i) => {
                if (stageWaypoints[wpIndex]?.routeOrder) {
                  const bumps = breakRanks.filter(b => b.rank <= i).length;
                  onUpdateRouteOrder(stageWaypoints[wpIndex].routeOrder.order.id, { ordering: i + bumps, via: [] });
                }
              });
              reanchorBreaks(breakRanks);
            }

            resolve(result);
          } else {
            reject({ status: 'error', error: status, index });
          }
        }
      );
    });
  };

  // ---------------------------------------------------------------------------
  // Dispatcher: select engine based on routingEngine parameter
  // ---------------------------------------------------------------------------
  const calculateAndRenderRouteOnMap = routingEngine === 'osrm'
    ? calculateRouteOsrm
    : calculateRouteGoogle;

  return {
    renderStagesOnMap,
    startPointLocation,
    endPointLocation,
    routePolylines,
    snappedPositions,
    roadSnappedPositions,
    routeDistance,
    routeDuration,
  };
};
